import React from 'react';

import {
  PaginationControls,
  PaginationControlsMode,
  PaginationControlsPageChange,
  PaginationControlsRowsPerPageChange,
} from 'components/atoms/PaginationControls';
import { useEncodedFilters } from 'utils/useEncodedFilters';
import { DEFAULT_PAGE_SIZE } from 'utils/useEncodedFilters/constants';
import { useProcedurePageLayout } from './useProcedurePageLayout';

export interface ProcedurePaginationProps {
  totalCases: number;
  totalSlides: number;
  isLoading?: boolean;
  dataGridPagination?: boolean;
  hideRowsPerPage?: boolean;
  alignRowsPerPageLeft?: boolean;
  showRowsPerPageSelector?: boolean;
  onPageChange?: (withOffset: boolean) => (event: React.ChangeEvent<unknown>, nextPage?: number) => void;
  onRowsPerPageChange?: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  forcePage?: number;
  forcePageSize?: number;
}

export const ProcedurePagination: React.FC<React.PropsWithChildren<ProcedurePaginationProps>> = ({
  totalCases,
  totalSlides,
  isLoading = false,
  dataGridPagination,
  hideRowsPerPage = false,
  alignRowsPerPageLeft = false,
  showRowsPerPageSelector = false,
  onPageChange,
  onRowsPerPageChange,
  forcePage,
  forcePageSize,
}) => {
  const { queryParams, setQueryParams } = useEncodedFilters();
  const { pageSizeOptions } = useProcedurePageLayout();
  const onPageChangeRef = React.useRef(onPageChange);
  const onRowsPerPageChangeRef = React.useRef(onRowsPerPageChange);
  const queryPageRef = React.useRef(queryParams.page || 1);
  const queryPageSizeRef = React.useRef(queryParams.pageSize || DEFAULT_PAGE_SIZE);

  onPageChangeRef.current = onPageChange;
  onRowsPerPageChangeRef.current = onRowsPerPageChange;
  queryPageRef.current = queryParams.page || 1;
  queryPageSizeRef.current = queryParams.pageSize || DEFAULT_PAGE_SIZE;

  const totalRows = queryParams?.slidesMode ? totalSlides : totalCases;
  const page = forcePage || queryParams.page || 1;
  const pageSize = forcePageSize || queryParams.pageSize || DEFAULT_PAGE_SIZE;
  const entityLabel = queryParams?.slidesMode ? 'slides' : 'cases';
  const mode: PaginationControlsMode = dataGridPagination
    ? 'data-grid'
    : showRowsPerPageSelector
    ? 'table'
    : 'standard';

  const handlePageChange = React.useCallback(
    ({ event, page: nextPage, source }: PaginationControlsPageChange) => {
      const useOffset = source === 'data-grid' || source === 'table';

      if (onPageChangeRef.current) {
        onPageChangeRef.current(useOffset)(event as React.ChangeEvent<unknown>, useOffset ? nextPage - 1 : nextPage);
        return;
      }

      if (queryPageRef.current !== nextPage) {
        setQueryParams({ page: nextPage });
      }
    },
    [setQueryParams]
  );

  const handleRowsPerPageChange = React.useCallback(
    ({ event, pageSize: nextPageSize }: PaginationControlsRowsPerPageChange) => {
      if (onRowsPerPageChangeRef.current) {
        onRowsPerPageChangeRef.current(event);
        return;
      }

      if (nextPageSize !== queryPageSizeRef.current) {
        setQueryParams({ pageSize: nextPageSize });
      }
    },
    [setQueryParams]
  );

  return (
    <PaginationControls
      totalRows={totalRows}
      page={page}
      pageSize={pageSize}
      pageSizeOptions={pageSizeOptions}
      entityLabel={entityLabel}
      isLoading={isLoading}
      mode={mode}
      hideRowsPerPage={hideRowsPerPage}
      alignRowsPerPageLeft={alignRowsPerPageLeft}
      onPageChange={handlePageChange}
      onRowsPerPageChange={handleRowsPerPageChange}
    />
  );
};
